// frontend/src/components/organization/FreeTierBanner.js
//
// Shows a countdown / upgrade nudge on Free-tier orgs during the 6-month lifetime:
//   • 30 days out: informational (info alert)
//   • 7 days out: warning (yellow)
//   • Past expiry, within 7-day grace: red urgent
//   • Beyond grace (soft-block active): red, translation is blocked
//
// Hidden on all paid tiers, and dismissible per-session for the "info" and
// "warning" bands (never dismissible once in grace or expired).

import React, { useMemo, useState } from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';

const DAY_MS = 24 * 60 * 60 * 1000;
const GRACE_DAYS = 7;

const dismissKey = (orgId, band) => `freeTierBanner:${orgId}:${band}`;

const FreeTierBanner = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [tick, setTick] = useState(0); // eslint-disable-line no-unused-vars

  const meta = useMemo(() => {
    if (!currentOrganization || currentOrganization.tier !== 'free') return null;
    const raw = currentOrganization.freeTierExpiresAt;
    if (!raw) return null;
    const expiresAt = new Date(raw);
    const now = new Date();
    const daysToExpiry = Math.ceil((expiresAt - now) / DAY_MS);
    const graceEnd = new Date(expiresAt.getTime() + GRACE_DAYS * DAY_MS);
    const daysToGraceEnd = Math.ceil((graceEnd - now) / DAY_MS);

    let band;
    if (now > graceEnd) band = 'expired';
    else if (now > expiresAt) band = 'grace';
    else if (daysToExpiry <= 7) band = 'warning';
    else if (daysToExpiry <= 30) band = 'info';
    else band = null;

    return { expiresAt, daysToExpiry, graceEnd, daysToGraceEnd, band };
  }, [currentOrganization, tick]);

  if (!meta || !meta.band) return null;

  const orgId = currentOrganization.id;
  const dismissed = ['info', 'warning'].includes(meta.band)
    && sessionStorage.getItem(dismissKey(orgId, meta.band)) === '1';
  if (dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(dismissKey(orgId, meta.band), '1');
    setTick(t => t + 1);
  };

  const cta = (
    <Button color="inherit" size="small" onClick={() => navigate('/organizations/upgrade')}>
      Upgrade Now
    </Button>
  );

  if (meta.band === 'expired') {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="error" action={cta}>
          <AlertTitle>Free plan has ended</AlertTitle>
          Your 6-month Free plan and 7-day grace period have both ended. Translation is paused.
          Reading, exporting, and downloading remain available. Upgrade to Starter or higher to resume translating.
        </Alert>
      </Box>
    );
  }

  if (meta.band === 'grace') {
    const daysLeft = Math.max(0, meta.daysToGraceEnd);
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="error" action={cta}>
          <AlertTitle>Grace period — {daysLeft} day{daysLeft === 1 ? '' : 's'} left</AlertTitle>
          Your Free plan expired on {meta.expiresAt.toLocaleDateString()}. You have {daysLeft} day{daysLeft === 1 ? '' : 's'}
          {' '}to upgrade before translation is paused.
        </Alert>
      </Box>
    );
  }

  if (meta.band === 'warning') {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="warning" onClose={dismiss} action={cta}>
          <AlertTitle>Free plan ends in {meta.daysToExpiry} day{meta.daysToExpiry === 1 ? '' : 's'}</AlertTitle>
          Upgrade to Starter or higher to keep translating without interruption. After {meta.expiresAt.toLocaleDateString()},
          a 7-day grace period applies before translation is paused.
        </Alert>
      </Box>
    );
  }

  // info
  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="info" onClose={dismiss} action={cta}>
        Your Free plan ends on {meta.expiresAt.toLocaleDateString()} ({meta.daysToExpiry} days away).
        Upgrade any time to unlock higher quota and remove the lifetime limit.
      </Alert>
    </Box>
  );
};

export default FreeTierBanner;
